import { useEffect, useState } from "react";
import { useListControls, ListControlsBar } from "@/components/ListSearchSort";
import { Link, useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, CalendarClock, MapPin, CheckCircle2, MailPlus } from "lucide-react";
import { useSport, SPORT_META } from "@/context/SportContext";
import { CourtChip } from "@/pages/Availability";

const fmtWhen = (iso) => {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
};
const durationLabel = (startIso, endIso, mins) => {
  if (mins) return `${mins}m`;
  const s = new Date(startIso).getTime(); const e = new Date(endIso).getTime();
  return `${Math.round((e - s) / 60000)}m`;
};

export default function StudentBookNext() {
  const { coachId } = useParams();
  const navigate = useNavigate();
  const { sport } = useSport();
  const meta = SPORT_META[sport] || SPORT_META.pickleball;
  const [state, setState] = useState({ loading: true, items: [], coach_name: "", error: null });
  const ctl = useListControls(state.items, { name: (it) => it.title || "", fields: (it) => [it.title, it.venue], initialSort: "default" });
  const [enrolling, setEnrolling] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/coach/${coachId}/availability?sport=${sport}`);
        if (!cancelled) setState({ loading: false, items: data.items, coach_name: data.coach_name, error: null });
      } catch (e) {
        if (!cancelled) setState({ loading: false, items: [], coach_name: "", error: formatApiError(e.response?.data?.detail) || "Could not load availability" });
      }
    })();
    return () => { cancelled = true; };
  }, [coachId, sport]);

  const enrolOffering = async (offeringId) => {
    setEnrolling(offeringId);
    try {
      const { data } = await api.post(`/offerings/${offeringId}/enrol`);
      if (data?.checkout_url) {
        // Stripe checkout flow — go pay
        window.location.href = data.checkout_url;
        return;
      }
      toast.success("Booked — see you on court!");
      navigate("/dashboard");
    } catch (e) {
      // Stripe returns 402 with the raw provider error when the key is
      // missing/rotated. Users shouldn't see that. Map the known
      // payment-service statuses to a friendly message and only surface the
      // raw detail for other 4xx/5xx.
      const status = e?.response?.status;
      const raw = e?.response?.data?.detail;
      if (status === 402 || (typeof raw === "string" && /stripe|checkout|payment|api key/i.test(raw))) {
        toast.error("Payments are temporarily unavailable — please try again in a bit or ask your coach to book you in.");
      } else {
        toast.error(formatApiError(raw) || "Booking failed");
      }
    } finally { setEnrolling(null); }
  };

  const hasSlots = state.items.length > 0;

  return (
    <div className="space-y-6">
      <div className="fade-up">
        <Link to="/dashboard" className="text-[#A3A3A3] hover:text-white inline-flex items-center gap-1 text-xs label-mono" data-testid="back-to-dashboard">
          <ArrowLeft className="w-3 h-3" /> Back to dashboard
        </Link>
        <div className="label-mono mt-3" style={{ color: meta.color }}>BOOK YOUR NEXT {meta.label.toUpperCase()} SESSION</div>
        <h1 className="h-display text-4xl md:text-5xl tracking-tighter mt-1">
          {state.coach_name ? `Slots with ${state.coach_name}` : "Available slots"}
        </h1>
        <div className="text-sm text-[#A3A3A3] mt-1">Choose an open window below — or ask for a new one if nothing works.</div>
      </div>

      {state.loading && <div className="text-sm text-[#A3A3A3]" data-testid="book-loading">Loading availability…</div>}

      {!state.loading && state.error && (
        <div className="card-surface p-6 text-sm text-[#FF4500]" data-testid="book-error">{state.error}</div>
      )}

      {!state.loading && !state.error && !hasSlots && (
        <div className="card-surface p-8 text-center space-y-4 fade-up d1" data-testid="book-empty">
          <CalendarClock className="w-10 h-10 mx-auto" style={{ color: meta.color }} strokeWidth={1.5} />
          <div className="h-display text-2xl">No slots yet — request one</div>
          <div className="text-sm text-[#A3A3A3] max-w-md mx-auto">
            {state.coach_name || "Your coach"} hasn&apos;t published any windows for the coming weeks. Send a request with 3+ dates that work for you and they&apos;ll come back to confirm.
          </div>
          <Link
            to="/requests?new=1"
            className="btn-volt py-2.5 px-6 text-xs inline-flex items-center justify-center gap-2"
            data-testid="request-session-cta"
          >
            <MailPlus className="w-4 h-4" /> Send a session request
          </Link>
        </div>
      )}

      {hasSlots && (
        <div className="space-y-3 fade-up d1" data-testid="book-slots">
          <ListControlsBar ctl={ctl} modes={["default", "az", "za"]} placeholder="Search sessions…" testid="book-next" />
          {ctl.list.map((it) => {
            const isOffering = it.kind === "offering";
            return (
              <div key={it.id} className="card-surface p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`slot-${it.kind}-${it.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-sm font-semibold">{fmtWhen(it.start)}</div>
                    <span className="text-[10px] uppercase tracking-wider label-mono px-2 py-0.5 border" style={{ color: SPORT_META[it.sport]?.color, borderColor: SPORT_META[it.sport]?.color }}>{it.sport}</span>
                    {isOffering ? (
                      <span className="text-[10px] uppercase tracking-wider label-mono px-2 py-0.5 border text-[#CCFF00] border-[#CCFF00] inline-flex items-center gap-1">
                        <CheckCircle2 className="w-3 h-3" /> Bookable
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-wider label-mono px-2 py-0.5 border text-[#A3A3A3] border-[#404040]">
                        Coach window
                      </span>
                    )}
                    <CourtChip value={it.court_responsibility} />
                  </div>
                  <div className="text-sm text-[#E5E5E5] mt-1">
                    {isOffering ? it.title : "Ad-hoc window — send a request to lock it in"}
                  </div>
                  <div className="text-xs text-[#A3A3A3] mt-1 flex items-center gap-3 flex-wrap">
                    <span>{durationLabel(it.start, it.end, it.duration_min)}</span>
                    {isOffering && <span>·</span>}
                    {isOffering && <span>£{it.price_per_player} / player</span>}
                    {isOffering && <span>·</span>}
                    {isOffering && <span>{it.spaces_left} left</span>}
                    {it.venue && (
                      <>
                        <span>·</span>
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> {it.venue}</span>
                      </>
                    )}
                  </div>
                  {it.notes && <div className="text-xs text-[#A3A3A3] mt-1 italic">{it.notes}</div>}
                </div>
                <div className="shrink-0">
                  {isOffering ? (
                    <button
                      onClick={() => enrolOffering(it.id)}
                      disabled={enrolling === it.id}
                      data-testid={`book-offering-${it.id}`}
                      className="btn-volt py-2.5 px-5 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {enrolling === it.id ? "Booking…" : "Book slot"}
                    </button>
                  ) : (
                    <Link
                      to={`/requests?new=1&window=${it.id}`}
                      className="border border-[#404040] hover:border-[#CCFF00] hover:text-[#CCFF00] py-2.5 px-5 text-xs uppercase tracking-wider label-mono inline-flex items-center justify-center gap-2 transition-colors"
                      data-testid={`request-window-${it.id}`}
                    >
                      <MailPlus className="w-4 h-4" /> Request this window
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
